<template>
  <div class="mobile-goods" id="mobile-goods">
    <header class="goods-header">
      <!-- <MobileNav /> -->

      <div class="goods-header__title">
        <img
          src="@/assets/image/ticket/nav_icon_star.webp"
          alt="Decorative lightning bolt image"
          width="140"
          height="140"
          class="goods-header__dynamic goods-header__dynamic-cd"
        />
        <img
          src="@/assets/image/index/mobile_header_lightning.webp"
          alt="Decorative lightning bolt image"
          width="96"
          height="96"
          decoding="async"
          class="goods-header__dynamic goods-header__dynamic-lightning"
        />

        <img
          class="goods-header__title-img"
          src="@/assets/image/goods/mobile_title.webp"
          alt="Goods page title graphic"
          width="769"
          height="66"
          decoding="async"
          loading="lazy"
        />
      </div>

      <div class="mobile-marquee mobile-marquee--banner">
        <div class="mobile-marquee-inner">
          <div class="mobile-marquee-track">
            <div
              v-for="dup in 2"
              :key="'mobile-marquee-banner-' + dup"
              class="mobile-marquee-group"
              :aria-hidden="dup === 2 ? 'true' : undefined"
            >
              <img
                v-for="item in 10"
                :key="`mobile-banner-${dup}-${item}`"
                class="mobile-marquee-title"
                src="@/assets/image/index/banner_footer_title.webp"
                width="200"
                height="14"
                alt="Scrolling footer title strip (decorative)"
                aria-hidden="true"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </div>
    </header>

    <div class="goods-body mobile-business-fade-up">
      <div
        v-if="mobileGoodsCopy.noteTitle || mobileGoodsCopy.noteText"
        class="goods-body__note"
      >
        <p v-if="mobileGoodsCopy.noteTitle" class="goods-body__note-title">
          {{ mobileGoodsCopy.noteTitle }}
        </p>
        <div v-if="mobileGoodsCopy.noteText" class="goods-body__note-text">
          {{ mobileGoodsCopy.noteText }}
        </div>
      </div>
      <div
        class="mobile-goods__list"
        :class="{
          'mobile-goods__list--empty': !goodsLoading && !goodsList.length,
        }"
      >
        <div
          v-if="goodsLoading"
          class="mobile-goods__list-loading"
          role="status"
          aria-live="polite"
        >
          <span class="mobile-goods__list-loading-spinner" aria-hidden="true" />
        </div>
        <template v-else-if="goodsList.length">
          <div
            v-for="(item, index) in paginatedList"
            :key="'goods-item-' + listSliceStart + index"
            class="mobile-goods__list-item"
            role="button"
            tabindex="0"
            @click="openGoodsDetail(item)"
            @keydown.enter.prevent="openGoodsDetail(item)"
            @keydown.space.prevent="openGoodsDetail(item)"
          >
            <div class="mobile-goods__list-item-img-container">
              <div
                v-if="isGoodsImageLoading(item, listSliceStart + index)"
                class="mobile-goods__list-item-img-loading"
                aria-hidden="true"
              >
                <span class="mobile-goods__list-item-img-spinner" />
              </div>
              <img
                class="mobile-goods__list-item-img"
                :class="{ 'is-loading': isGoodsImageLoading(item, listSliceStart + index) }"
                :src="item.img[0]"
                width="160"
                height="160"
                :alt="'Merchandise product image — ' + item.title"
                decoding="async"
                loading="lazy"
                @load="onGoodsImageLoad(item, listSliceStart + index)"
                @error="onGoodsImageError(item, listSliceStart + index)"
              />
            </div>

            <div class="mobile-goods__list-item-add-icon">+</div>
            <div class="mobile-goods__list-item-content">
              <div class="mobile-goods__list-item-info">
                <div
                  v-for="(line, lineIndex) in item.title"
                  :key="lineIndex"
                  class="mobile-goods__list-item-info-msg"
                >
                  {{ line }}
                </div>
              </div>
              <div
                class="mobile-goods__list-item-price"
                :class="{
                  'mobile-goods__list-item-price--plain': !item.priceIsNumeric,
                }"
              >
                <template v-if="item.priceIsNumeric">
                  <span class="mobile-goods__list-item-price-label">{{
                    mobileGoodsCopy.pricePrefix
                  }}</span>
                  <div class="mobile-goods__list-item-price-col">
                    <span class="mobile-goods__list-item-price-amount"
                      >{{ item.price }} {{ mobileGoodsCopy.yenUnit
                      }}<span
                        v-if="mobileGoodsCopy.taxIncluded"
                        class="mobile-goods__list-item-price-tax"
                        >{{ mobileGoodsCopy.taxIncluded }}</span
                      ></span
                    >
                  </div>
                </template>
                <span v-else class="mobile-goods__list-item-price-plain">{{
                  item.price
                }}</span>
              </div>
            </div>
          </div>
        </template>
        <div
          v-else
          class="mobile-goods__list-empty"
          role="status"
          aria-live="polite"
        >
          <p class="mobile-goods__list-empty-title">
            {{ mobileGoodsCopy.emptyTitle }}
          </p>
          <p class="mobile-goods__list-empty-desc">
            {{ mobileGoodsCopy.emptyDesc }}
          </p>
        </div>
      </div>

      <nav
        v-if="!goodsLoading && goodsList.length"
        class="mobile-goods__pagination"
        :aria-label="
          mobileGoodsCopy.paginationAriaLabel || 'Goods list pagination'
        "
      >
        <button
          type="button"
          class="mobile-goods__pagination-nav"
          :disabled="currentPage <= 1"
          :aria-label="
            mobileGoodsCopy.ariaPrevPage || 'Previous page'
          "
          @click="goToPage(currentPage - 1)"
        >
          <svg
            width="8"
            height="13"
            viewBox="0 0 8 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.07129 12.0207L1.41443 6.36389L7.07129 0.707031"
              stroke="#FFF380"
              stroke-width="2"
            />
          </svg>
        </button>
        <ul class="mobile-goods__pagination-pages">
          <li v-for="p in totalPages" :key="'goods-page-' + p">
            <button
              type="button"
              class="mobile-goods__pagination-page"
              :class="{ 'is-active': currentPage === p }"
              :aria-current="currentPage === p ? 'page' : undefined"
              :aria-label="
            mobileGoodsCopy.ariaPagePrefix
              ? mobileGoodsCopy.ariaPagePrefix + p
              : 'Page ' + p
          "
              @click="goToPage(p)"
            >
              {{ p }}
            </button>
          </li>
        </ul>
        <button
          type="button"
          class="mobile-goods__pagination-nav mobile-goods__pagination-nav--next"
          :disabled="currentPage >= totalPages"
          :aria-label="mobileGoodsCopy.ariaNextPage || 'Next page'"
          @click="goToPage(currentPage + 1)"
        >
          <svg
            width="8"
            height="13"
            viewBox="0 0 8 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.707031 12.0207L6.36389 6.36389L0.707031 0.707031"
              stroke="#FFF380"
              stroke-width="2"
            />
          </svg>
        </button>
      </nav>
    </div>

    <PcGoodsDetailModal
      v-if="goodsDetailVisible"
      :goods-list="goodsList"
      :active-index="goodsDetailIndex"
      :can-prev="goodsDetailCanPrev"
      :can-next="goodsDetailCanNext"
      @update:visible="onGoodsDetailVisible"
      @prev="goGoodsDetailPrev"
      @next="goGoodsDetailNext"
      @change-index="onGoodsDetailSwiperIndex"
    />
  </div>
</template>

<script>
import { scrollToAnchorById } from "@/utils/mobile-anchor";
import PcGoodsDetailModal from "./detail.vue";
import goodsContent from "@/views/data/goods-content.json";
import { fetchGoodsList, parseContentListResponse } from "@/api/goods";
import {
  hydrateGoodsListFromContent,
  mapGoodsRowToItem,
} from "@/utils/goods-list-api";

const GOODS_PAGE_SIZE = 16;

/**
 * 移动端商品页：接口 goods + goods-content.json 兜底；顶部注意書きは JSON に無いため非表示
 */
export default {
  name: "MGoods",
  components: {
    PcGoodsDetailModal,
  },

  data() {
    const gi = goodsContent.goodsIndex;
    return {
      mobileGoodsCopy: {
        ...gi,
        noteTitle: "",
        noteText: "",
        pricePrefix: gi.priceLabel,
        yenUnit: "円",
        taxIncluded: gi.taxNote,
      },
      goodsList: [],
      goodsLoading: true,
      currentPage: 1,
      goodsDetailVisible: false,
      goodsDetailIndex: -1,
      goodsDetailScrollRestoreY: 0,
      /** 列表首图加载状态：key -> loading | loaded | error */
      goodsImageLoadStatus: {},
    };
  },

  computed: {
    goodsDetailCanPrev() {
      return this.goodsDetailIndex > 0;
    },
    goodsDetailCanNext() {
      return (
        this.goodsDetailIndex >= 0 &&
        this.goodsDetailIndex < this.goodsList.length - 1
      );
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.goodsList.length / GOODS_PAGE_SIZE));
    },
    listSliceStart() {
      return (this.currentPage - 1) * GOODS_PAGE_SIZE;
    },
    paginatedList() {
      return this.goodsList.slice(
        this.listSliceStart,
        this.listSliceStart + GOODS_PAGE_SIZE,
      );
    },
  },

  watch: {
    goodsList() {
      this.clampCurrentPage();
    },
    totalPages() {
      this.clampCurrentPage();
    },
    paginatedList: {
      immediate: true,
      handler() {
        this.ensureVisibleGoodsImageLoadStatus();
      },
    },
  },

  mounted() {
    this.$nextTick(() => {
      scrollToAnchorById("mobile-goods", {
        behavior: "auto",
        block: "start",
      });
    });
  },

  created() {
    this.loadGoodsList();
  },

  methods: {
    async loadGoodsList() {
      this.goodsLoading = true;
      try {
        const res = await fetchGoodsList({ index: 1, size: 200 });
        const { rows } = parseContentListResponse(res);
        this.goodsList = rows.map(mapGoodsRowToItem);
      } catch (e) {
        console.error("[goods] fetchGoodsList", e);
        this.goodsList = hydrateGoodsListFromContent(goodsContent.goodsList);
      } finally {
        this.goodsLoading = false;
      }
    },
    goodsImageStatusKey(item, absoluteIndex) {
      const src = item && Array.isArray(item.img) && item.img[0] ? item.img[0] : "";
      return `${absoluteIndex}:${src}`;
    },
    ensureVisibleGoodsImageLoadStatus() {
      this.paginatedList.forEach((item, index) => {
        const absoluteIndex = this.listSliceStart + index;
        const key = this.goodsImageStatusKey(item, absoluteIndex);
        if (!this.goodsImageLoadStatus[key]) {
          this.$set(this.goodsImageLoadStatus, key, "loading");
        }
      });
    },
    isGoodsImageLoading(item, absoluteIndex) {
      const key = this.goodsImageStatusKey(item, absoluteIndex);
      const status = this.goodsImageLoadStatus[key];
      return !status || status === "loading";
    },
    onGoodsImageLoad(item, absoluteIndex) {
      const key = this.goodsImageStatusKey(item, absoluteIndex);
      this.$set(this.goodsImageLoadStatus, key, "loaded");
    },
    onGoodsImageError(item, absoluteIndex) {
      const key = this.goodsImageStatusKey(item, absoluteIndex);
      this.$set(this.goodsImageLoadStatus, key, "error");
    },
    clampCurrentPage() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
      if (this.currentPage < 1) {
        this.currentPage = 1;
      }
    },
    goToPage(page) {
      const p = Number(page);
      if (p < 1 || p > this.totalPages || p === this.currentPage) return;
      this.currentPage = p;
      this.scrollGoodsPageTop();
    },
    scrollGoodsPageTop() {
      const behavior = window.matchMedia("(prefers-reduced-motion: reduce)")
        .matches
        ? "auto"
        : "smooth";
      window.scrollTo({ top: 0, left: 0, behavior });
    },
    openGoodsDetail(item) {
      if (typeof window !== "undefined") {
        this.goodsDetailScrollRestoreY =
          window.scrollY ||
          window.pageYOffset ||
          (typeof document !== "undefined"
            ? document.documentElement.scrollTop
            : 0) ||
          0;
      }
      const i = this.goodsList.indexOf(item);
      this.goodsDetailIndex = i >= 0 ? i : 0;
      this.goodsDetailVisible = true;
    },
    onGoodsDetailVisible(open) {
      this.goodsDetailVisible = open;
      if (!open && typeof window !== "undefined") {
        const y = this.goodsDetailScrollRestoreY;
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            window.scrollTo({ top: y, left: 0, behavior: "auto" });
          });
        });
      }
    },
    goGoodsDetailPrev() {
      if (!this.goodsDetailCanPrev) return;
      this.goodsDetailIndex -= 1;
    },
    goGoodsDetailNext() {
      if (!this.goodsDetailCanNext) return;
      this.goodsDetailIndex += 1;
    },
    onGoodsDetailSwiperIndex(i) {
      const n = Number(i);
      if (n < 0 || n >= this.goodsList.length) return;
      this.goodsDetailIndex = n;
    },
  },
};
</script>

<style scoped lang="scss">
.mobile-goods {
  color: #fff;
  font-family: "Noto Sans JP", "Noto Sans", sans-serif;

  position: relative;
  background-image: url("@/assets/image/streaming/mobile_bg.webp");
  background-size: cover;
  background-repeat: no-repeat;
  background-attachment: scroll;
  // z-index: 1;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    top: 230px;
    width: 100%;
    height: 100%;
    background-image: url(@/assets/image/streaming/mobile_bg_icon.webp);
    background-position: top -30px left -30px; /* 保持你要的左上角对齐 */
    background-size: 140% auto;
    background-repeat: no-repeat;
    animation: BackgroundFloat 6s ease-in-out infinite;
    z-index: 1;
  }

  box-sizing: border-box;
  background-color: #3c3068;
  text-align: left;

  img {
    box-sizing: border-box;
    vertical-align: middle;
  }

  .goods-header {
    position: relative;
    width: 100%;
    box-sizing: border-box;
    z-index: 2;

    &__background {
      position: absolute;

      width: 100%;
      object-fit: contain;
      object-position: center;
    }

    &__dynamic {
      position: absolute;
      object-fit: contain;
      object-position: center;
      transform-origin: center center;
      will-change: transform;
      animation: mobile-header-img-dynamic 1.35s infinite;

      position: absolute;
      z-index: 9;

      &-cd {
        top: 10px;
        left: -10px;
      }
      &-lightning {
        top: 90px;
        right: 30px;
      }
    }

    &__title {
      padding: 90px 140px 60px;
    }

    &__deco {
      position: absolute;
      object-fit: contain;
      object-position: center;
      transform-origin: center center;
      will-change: transform;
      animation: ticket-deco-beat 1.35s infinite;

      &-circular {
        top: 39px;
        left: 204px;
        width: 300px;
      }

      &-star {
        top: 71px;
        right: 188px;
        width: 227px;
      }
    }
  }

  .goods-body {
    position: relative;
    z-index: 2;
    padding: 50px;

    &__note {
      margin-bottom: 56px;
      padding: 32px 30px;
      font-size: 14px;
      border: 2px solid #fff380;
      border-radius: 8px;
      background: rgba(0, 0, 0, 0.25);

      &-title {
        margin-bottom: 20px;
        color: #37fff1;
        font-weight: 600;
        line-height: 100%;
      }

      &-text {
        color: #fff;
        font-weight: 500;
        line-height: 150%;
      }
    }
  }
  .mobile-goods__list {
    position: relative;
    z-index: 10;
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    display: grid;
    grid-template-columns: 160px 160px;
    gap: 20px;
    // padding: 0 320px;

    &-loading {
      grid-column: 1 / -1;
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 160px;
    }

    &-loading-spinner {
      width: 36px;
      height: 36px;
      border: 3px solid rgba(255, 243, 128, 0.25);
      border-top-color: #fff380;
      border-radius: 50%;
      animation: mobile-goods-loading-spin 0.75s linear infinite;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
        border-color: #fff380;
        opacity: 0.85;
      }
    }

    &--empty {
      display: block;
      flex-wrap: nowrap;
      justify-content: center;
      align-items: center;
      min-height: 360px;
      padding-top: 48px;
      padding-bottom: 72px;
      box-sizing: border-box;
    }

    &-empty {
      width: 100%;
      max-width: 720px;
      margin: 0 auto;
      padding: 40px 32px 44px;
      text-align: center;
      background-color: #191616;
      border: 2px solid #fff380;
      border-radius: 8px;
      box-sizing: border-box;
    }

    &-empty-title {
      margin: 0 0 16px;
      font-size: 20px;
      font-weight: 700;
      letter-spacing: 0.12em;
      color: #fff380;
    }

    &-empty-desc {
      margin: 0;
      font-size: 15px;
      font-weight: 600;
      line-height: 1.65;
      color: rgba(255, 255, 255, 0.92);
    }

    &-item {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      //   width: 280px;
      width: 100%;
      object-fit: contain;
      object-position: center;
      border-radius: 8px;
      border: 2px solid #fff380;
      text-align: center;
      background-color: #191616;
      cursor: pointer;
      box-sizing: border-box;
      transition: transform 0.28s cubic-bezier(0.25, 0.82, 0.25, 1),
        box-shadow 0.28s ease, border-color 0.28s ease;
      overflow: hidden;

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }

      // &:hover {
      //   transform: translateY(-8px);
      //   border-color: #fff9b0;
      //   box-shadow: 0 16px 32px rgba(0, 0, 0, 0.5),
      //     0 0 0 1px rgba(255, 243, 128, 0.4), 0 0 28px rgba(255, 243, 128, 0.28);
      //   z-index: 1;
      // }

      // &:active {
      //   transform: translateY(-3px) scale(0.985);
      //   box-shadow: 0 8px 20px rgba(0, 0, 0, 0.45),
      //     0 0 18px rgba(255, 243, 128, 0.2);
      //   transition-duration: 0.12s;
      // }

      // @media (prefers-reduced-motion: reduce) {
      //   &:hover,
      //   &:active {
      //     transform: none;
      //     box-shadow: none;
      //   }
      // }

      .mobile-goods__list-item-img-container {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 160px;
        background-color: #191616;
      }

      .mobile-goods__list-item-img-loading {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(
          180deg,
          rgba(25, 22, 22, 0.7) 0%,
          rgba(25, 22, 22, 0.42) 100%
        );
        z-index: 1;
        pointer-events: none;
      }

      .mobile-goods__list-item-img-spinner {
        width: 24px;
        height: 24px;
        border: 3px solid rgba(255, 243, 128, 0.3);
        border-top-color: #fff380;
        border-radius: 50%;
        animation: mobile-goods-img-spin 0.9s linear infinite;
      }

      .mobile-goods__list-item-img {
        display: block;
        flex-shrink: 0;
        width: 100%;
        height: 90%;
        object-fit: contain;
        object-position: center;
        transition: opacity 0.2s ease;

        &.is-loading {
          opacity: 0;
        }
      }

      &-add-icon {
        position: absolute;
        top: 128px;
        right: 2px;
        width: 30px;
        height: 30px;
        background: rgba(166, 166, 166, 0.5);
        border-radius: 50%;
        text-align: center;
        line-height: 30px;
        font-size: 30px;
        color: #fff380;
      }

      &-content {
        width: 100%;
        padding: 10px 12px 16px;
        align-self: stretch;
        box-sizing: border-box;
        background-color: #fff380;
      }

      &-info {
        margin-bottom: 16px;
        min-height: 42px;
        color: #191616;
        text-align: left;

        font-weight: 600;
        font-size: 12px;
        line-height: 120%;
        letter-spacing: 0%;
      }

      &-info-msg + &-info-msg {
        margin-top: 4px;
      }
      &-price {
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 8px;
        width: 100%;
        box-sizing: border-box;
        font-weight: 700;
        color: #191616;

        &--plain {
          justify-content: flex-end;
        }
      }

      &-price-plain {
        width: 100%;
        text-align: right;
        font-size: 14px;
        line-height: 1.35;
        font-weight: 700;
        word-break: break-word;
        overflow-wrap: anywhere;
        color: #191616;
      }

      &-price-label {
        flex: 0 0 4em;
        width: 3em;
        max-width: 3em;
        padding-top: 2px;
        box-sizing: border-box;
        font-size: 10px;
        line-height: 1.2;
        letter-spacing: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        text-align: left;
      }

      &-price-col {
        flex: 1;
        min-width: 0;
        text-align: right;
      }

      /* 金额 + 税注同一流式块；宽度不足时自动换行，高度随内容（一行或两行等） */
      &-price-amount {
        display: block;
        width: 100%;
        font-size: 14px;
        line-height: 1.35;
        word-break: break-all;
        overflow-wrap: anywhere;
      }

      &-price-tax {
        display: inline;
        margin-left: 2px;
        font-size: 10px;
        line-height: 1.35;
        font-weight: 700;
        white-space: nowrap;
        vertical-align: baseline;
      }
    }
  }

  &__pagination {
    position: relative;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    margin-top: 30px;
    // padding: 24px 320px 48px;
    box-sizing: border-box;
  }

  &__pagination-nav {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.12);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: background-color 0.2s ease, opacity 0.2s ease,
      transform 0.18s ease;

    &:hover:not(:disabled) {
      background-color: rgba(255, 255, 255, 0.2);
    }

    &:active:not(:disabled) {
      transform: scale(0.9);
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }

    &--next .mobile-goods__pagination-chevron {
      transform: rotate(225deg);
    }
  }

  &__pagination-pages {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__pagination-page {
    position: relative;
    min-width: 1.25em;
    padding: 0 4px 0;
    border: none;
    background: none;
    font-size: 18px;
    font-weight: 500;
    line-height: 1;
    color: #fff;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: color 0.2s ease, opacity 0.2s ease, transform 0.18s ease;

    &:active {
      transform: scale(0.92);
    }

    &::after {
      content: "";
      position: absolute;
      left: 50%;
      bottom: -10px;
      width: 0;
      height: 2px;
      border-radius: 2px;
      background-color: #fff380;
      transform: translateX(-50%);
      transition: width 0.2s ease;
    }

    &:hover:not(.is-active) {
      opacity: 0.85;
    }

    &.is-active {
      &::after {
        width: 12px;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .mobile-goods__pagination-nav {
      transition: background-color 0.2s ease, opacity 0.2s ease;
      &:active:not(:disabled) {
        transform: none;
      }
    }
    .mobile-goods__pagination-page {
      transition: color 0.2s ease, opacity 0.2s ease;
      &:active {
        transform: none;
      }
    }
  }
}

/* 无限滚动横幅 */
.mobile-marquee {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 60px;
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;

  // background-image: url("@/assets/image/index/about_footer_bg.webp");
  //   background-image: url("@/assets/image/attention/header_footer_title_bg.webp");
  background-image: url("@/assets/image/index/banner_footer_bg.webp");

  &--description {
    position: absolute;
    left: 0;
    right: 0;
    bottom: -28px;
    z-index: 9;
  }

  &-inner {
    width: 100%;
    height: 24px;
    overflow: hidden;
    background-color: #191616;
    display: flex;
    align-items: center;
    border-top: 2px solid #fff590;
    border-bottom: 2px solid #fff590;
  }

  &-track {
    display: flex;
    width: max-content;
    flex-shrink: 0;
    align-items: center;
    animation: mobile-marquee 30s linear infinite;
    will-change: transform;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      will-change: auto;
    }
  }

  &-group {
    display: flex;
    flex-shrink: 0;
    align-items: center;
  }

  &-title {
    flex-shrink: 0;
    margin-right: 20px;
    width: 200px;
    height: 10px;
    object-fit: contain;
    object-position: center;
  }
}

/* 无限滚动动画 */
@keyframes mobile-marquee {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

@keyframes mobile-goods-img-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes mobile-goods-loading-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes mobile-header-img-dynamic {
  0%,
  42% {
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.45, 0, 0.55, 1);
  }
  52% {
    transform: scale(0.9);
    animation-timing-function: cubic-bezier(0.55, 0.05, 0.6, 0.45);
  }
  58% {
    transform: scale(0.86);
    animation-timing-function: cubic-bezier(0.33, 1.15, 0.52, 1);
  }
  70% {
    transform: scale(1.08);
    animation-timing-function: cubic-bezier(0.22, 0.85, 0.38, 1);
  }
  80% {
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes BackgroundFloat {
  0% {
    background-position: top -30px left -30px;
    background-size: 140% auto;
    opacity: 0.85;
  }

  50% {
    /* 中心对称放大 → 向四周扩散 */
    background-position: top -60px left -60px;
    background-size: 150% auto;
    opacity: 1;
  }

  100% {
    /* 回到初始位置 */
    background-position: top -30px left -30px;
    background-size: 140% auto;
    opacity: 0.85;
  }
}
</style>
